import { getActivityHistory, getActivityPreview, type ActivityFilters } from '../lib/api';
import type { AuditCategory, JobView } from '@api/devices/contracts';
import type { RecordKind, SearchHit, SearchQueryInput } from '@contracts';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getAuditTrail, getCatalog, getDeviceActivity, getJobBatch, listAgentReleases, getDeviceJobs, getEvidence, getJob, getRecord, getRemoteStatus, getSoftware, getTelemetry,
  listApprovalKeys, listEnrollmentGrants, listFinishedJobs, listJobs, searchFacets, searchRecords } from '../lib/api';
import { DAY, minuteFloor } from '../lib/time';
import { useScope } from '../scope/scope';

/** Remote session windows announce starts and ends here (remote/ViewerPage.tsx). */
export const REMOTE_CHANNEL = 'wittco-remote-sessions';

// Every scoped query key starts with ['scope', mspId] so a scope change can drop
// all of them at once and no two workspaces ever share a cache entry.

export const activityQueryKeys = ['activity', 'activity-preview', 'activity-history'];

export function useSearch(query: SearchQueryInput | null, { keepPrevious = true } = {}) {
  const { mspId } = useScope();
  return useQuery({
    queryKey: ['scope', mspId, 'search', query], enabled: query !== null,
    queryFn: ({ signal }) => searchRecords(mspId, query!, signal),
    placeholderData: keepPrevious ? keepPreviousData : undefined, staleTime: 15_000,
  });
}

export function useFacets(query: SearchQueryInput | null) {
  const { mspId } = useScope();
  return useQuery({
    queryKey: ['scope', mspId, 'facets', query], enabled: query !== null,
    queryFn: ({ signal }) => searchFacets(mspId, query!, signal),
    placeholderData: keepPreviousData, staleTime: 15_000,
  });
}

export function useRecord(kind: RecordKind, id: string | undefined) {
  const { mspId } = useScope();
  return useQuery({
    queryKey: ['scope', mspId, 'record', kind, id], enabled: !!id,
    queryFn: ({ signal }) => getRecord(mspId, kind, id!, signal), staleTime: 15_000,
    // A device's status and last check-in change on their own: an open page follows them (WRMM-88).
    refetchInterval: kind === 'device' ? 30_000 : false,
  });
}

export function useEvidence(query: SearchQueryInput | null) {
  const { mspId } = useScope();
  return useQuery({
    queryKey: ['scope', mspId, 'evidence', query], enabled: query !== null,
    queryFn: ({ signal }) => getEvidence(mspId, query!, signal), staleTime: 30_000,
  });
}

/** Customer and site names in scope, for filters and question interpretation. */
export function useCatalog() {
  const { mspId } = useScope();
  const query = useQuery({
    queryKey: ['scope', mspId, 'catalog'], staleTime: 5 * 60_000,
    queryFn: ({ signal }) => searchFacets(mspId, { version: 1, kinds: ['organization', 'site'] }, signal),
  });
  return useMemo(() => {
    const organizations = query.data?.organizations.map(o => ({ id: o.id, title: o.title })) ?? [];
    const sites = query.data?.sites.map(s => ({ id: s.id, title: s.title, organizationId: s.organizationId })) ?? [];
    const orgNames = new Map(organizations.map(o => [o.id, o.title]));
    const siteNames = new Map(sites.map(s => [s.id, s.title]));
    return { ...query, catalog: { organizations, sites }, orgName: (id: string) => orgNames.get(id), siteName: (id: string) => siteNames.get(id) };
  }, [query]);
}

/** Whether one device is among needsLookQuery's: the same rule, for a device already in hand (WRMM-255). */
export function needsLook(hit: SearchHit, asOf: number) {
  if (hit.status === 'offline') return hit.role === 'server';
  return hit.status !== 'healthy' || !hit.lastSeenAt || Date.parse(hit.lastSeenAt) < asOf - 7 * DAY;
}

/** Devices with a reported problem, no status, or no check-in for a week; offline counts only for a server (WRMM-255). */
export function needsLookQuery(now = minuteFloor()): SearchQueryInput {
  return {
    version: 1, kinds: ['device'], sort: { field: 'status', direction: 'asc' }, limit: 100,
    filters: [{ mode: 'or', clauses: [
      { field: 'status', op: 'in', values: ['attention', 'offline', 'unknown'] },
      { field: 'lastSeenAt', op: 'before', value: new Date(now - 7 * DAY).toISOString() },
      { field: 'lastSeenAt', op: 'unknown' },
    ] },
    // Offline is a status, not something to look at, except on a server (WRMM-255, Harry 2026-10-24): an offline
    // workstation or laptop, however long it has been gone, isn't counted; the device list still shows it Offline.
    { mode: 'or', clauses: [{ field: 'status', op: 'in', values: ['healthy', 'attention', 'unknown'] }, { field: 'role', op: 'in', values: ['server'] }] }],
  };
}
export const fleetQuery: SearchQueryInput = { version: 1, kinds: ['device'] };
/** Every offline device, servers and workstations alike: for showing offline as a status (WRMM-255). */
export const offlineQuery: SearchQueryInput = { version: 1, kinds: ['device'], filters: [{ mode: 'and', clauses: [{ field: 'status', op: 'in', values: ['offline'] }] }] };

// ---- Devices and operations ----

const active = (job: JobView) => ['awaiting_approval', 'queued', 'delivered', 'running'].includes(job.state);

export function useTelemetry(deviceId: string | undefined, range: '24h' | '7d') {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'telemetry', deviceId, range], enabled: !!deviceId, placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getTelemetry(mspId, deviceId!, range, signal), staleTime: 15_000, refetchInterval: 30_000 });
}
export function useSoftware(deviceId: string, q: string) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'software', deviceId, q], placeholderData: keepPreviousData,
    queryFn: ({ signal }) => getSoftware(mspId, deviceId, q, signal), staleTime: 60_000 });
}
/** Jobs poll quickly while any is still moving, so results appear without a refresh. */
/** A device's WITTCO Remote status; checked often while it's being installed or a session is running. */
export function useRemoteStatus(deviceId: string | undefined) {
  const { mspId } = useScope();
  const client = useQueryClient();
  // The viewer runs in its own window and says when a session starts or ends there, so this page updates at once.
  useEffect(() => {
    if (!deviceId || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(REMOTE_CHANNEL);
    channel.onmessage = event => { if (event.data?.deviceId === deviceId) void client.invalidateQueries({ queryKey: ['scope', mspId, 'remote', deviceId] }); };
    return () => channel.close();
  }, [client, mspId, deviceId]);
  return useQuery({ queryKey: ['scope', mspId, 'remote', deviceId], enabled: !!deviceId,
    queryFn: ({ signal }) => getRemoteStatus(mspId, deviceId!, signal),
    refetchInterval: q => (q.state.data && (q.state.data.state === 'installing' || q.state.data.sessions.some(s => s.active)) ? 5000 : 30_000) });
}
export function useDeviceJobs(deviceId: string | undefined) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'device-jobs', deviceId], enabled: !!deviceId,
    queryFn: ({ signal }) => getDeviceJobs(mspId, deviceId!, signal),
    refetchInterval: q => (q.state.data?.some(active) ? 2000 : 30_000) });
}
export function useJobs() {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'jobs'], queryFn: ({ signal }) => listJobs(mspId, signal),
    refetchInterval: q => (q.state.data?.some(active) ? 3000 : 30_000) });
}
/** One page of finished jobs. The sidebar keeps polling the bounded list; this is the rest of the history. */
export function useFinishedJobs(q: string, page: number) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'jobs-history', q, page], placeholderData: keepPreviousData,
    queryFn: ({ signal }) => listFinishedJobs(mspId, { q, page }, signal),
    refetchInterval: 30_000 });
}
export function useJob(id: string | null) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'job', id], enabled: !!id, queryFn: ({ signal }) => getJob(mspId, id!, signal),
    refetchInterval: q => (q.state.data && active(q.state.data) ? 1500 : false) });
}
export function useDeviceActivity(deviceId: string | undefined) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'activity', deviceId], enabled: !!deviceId, queryFn: ({ signal }) => getDeviceActivity(mspId, deviceId!, signal), staleTime: 15_000 });
}
/** The workspace audit trail, newest first, a page at a time. */
export function useAuditTrail(category: AuditCategory | undefined) {
  const { mspId } = useScope();
  return useInfiniteQuery({ queryKey: ['scope', mspId, 'audit', category ?? 'all'], initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam, signal }) => getAuditTrail(mspId, { before: pageParam, category }, signal),
    getNextPageParam: page => page.nextBefore ?? undefined, staleTime: 15_000 });
}
export function useCatalogRunbooks() {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'runbooks'], queryFn: ({ signal }) => getCatalog(mspId, signal), staleTime: Infinity });
}
export function useJobBatch(id: string | null) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'batch', id], enabled: !!id, queryFn: ({ signal }) => getJobBatch(mspId, id!, signal),
    refetchInterval: q => (q.state.data?.jobs.some(active) ? 2000 : false) });
}
/** Signed agent releases devices can be updated to, newest first. */
export function useAgentReleases(enabled: boolean) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'agent-releases'], enabled, queryFn: ({ signal }) => listAgentReleases(mspId, signal), staleTime: 300_000 });
}
export const useApprovalKeys = () => useQuery({ queryKey: ['approval-keys'], queryFn: ({ signal }) => listApprovalKeys(signal) });
export function useEnrollmentGrants(enabled: boolean) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'grants'], enabled, queryFn: ({ signal }) => listEnrollmentGrants(mspId, signal) });
}

export function useDeviceActivityPreview(deviceId: string) {
  const { mspId } = useScope();
  return useQuery({ queryKey: ['scope', mspId, 'activity-preview', deviceId],
    queryFn: ({ signal }) => getActivityPreview(mspId, deviceId, signal), staleTime: 30_000, refetchInterval: 60_000 });
}
export function useDeviceActivityHistory(deviceId: string, filters: ActivityFilters) {
  const { mspId } = useScope();
  return useInfiniteQuery({ queryKey: ['scope', mspId, 'activity-history', deviceId, filters], initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => getActivityHistory(mspId, deviceId, filters, pageParam, signal),
    getNextPageParam: page => page.nextBefore ?? undefined, staleTime: 30_000 });
}
